/**
 * Display shaping for the Sample Plan Copy screen (#/SamplePlan-Copy): turns the SamplePlanService
 * (headlight-lims-sampleplan) specification-set reports, preview stats and load results into template-ready fields.
 * Pure functions, no DOM.
 */

/** @type {Record<string, string>} */
const ENTITY_LABELS = (
	{
		Contract: 'Contract specification sets',
		LineItem: 'Line item pay items',
		ProjectLineItemMaterialTest: 'Sample plan tests',
		MaterialLineItemJoin: 'Line item materials',
	});

/** @type {Record<string, string>} */
const LOAD_STATUS_LABELS = (
	{
		'Loaded': 'Saved',
		'Errors': 'Some failed',
		'Not attempted': 'Skipped (an earlier step failed)',
	});

/**
 * @typedef {Object} ProjectSetsDisplay
 * @property {boolean} Known - True when the project resolves to one item specification set.
 * @property {number} ItemIDTestSpecificationSet
 * @property {number} MaterialIDTestSpecificationSet
 * @property {Array<number>} PayItemSetIDs - The sets the project's line item pay items are on.
 * @property {string} ItemLabel
 * @property {string} MaterialLabel
 * @property {string} BasisLabel - What the sets were read from.
 */

/**
 * @typedef {Object} CountRow
 * @property {string} Label
 * @property {number} Count
 */

/**
 * @param {number} pIDTestSpecificationSet - A set ID.
 * @param {Record<string, string>} pNames - Set names by ID.
 * @return {string} A display label.
 */
function formatSetLabel(pIDTestSpecificationSet, pNames)
{
	if (!(pIDTestSpecificationSet > 0))
	{
		return 'None';
	}
	return (pNames || {})[pIDTestSpecificationSet] || `Specification set ${pIDTestSpecificationSet}`;
}

/**
 * @param {Record<string, any>} pSets - A ProjectSpecificationSets from SamplePlanService.
 * @param {Record<string, string>} pNames - Set names by ID.
 * @return {ProjectSetsDisplay} Display fields for one project.
 */
function formatProjectSets(pSets, pNames)
{
	let tmpBasisLabel = 'Unknown: no contract, and the line items do not share one specification set';
	if (pSets.Basis === 'Contract')
	{
		tmpBasisLabel = `Its contract, ${pSets.Contract.Name || pSets.Contract.IDContract}`;
	}
	else if (pSets.Basis === 'PayItems')
	{
		tmpBasisLabel = 'Its line items\' pay items (no contract)';
	}
	return (
		{
			Known: pSets.ItemIDTestSpecificationSet > 0,
			ItemIDTestSpecificationSet: pSets.ItemIDTestSpecificationSet,
			MaterialIDTestSpecificationSet: pSets.MaterialIDTestSpecificationSet,
			PayItemSetIDs: Object.keys(pSets.PayItemSetDistribution || {}).map((pID) => parseInt(pID, 10)),
			ItemLabel: formatSetLabel(pSets.ItemIDTestSpecificationSet, pNames),
			MaterialLabel: formatSetLabel(pSets.MaterialIDTestSpecificationSet, pNames),
			BasisLabel: tmpBasisLabel,
		});
}

/**
 * @param {ProjectSetsDisplay|null} pSource - Source display sets.
 * @param {ProjectSetsDisplay|null} pDest - Destination display sets.
 * @return {boolean} True when a copy between them would realign the destination.
 */
function isSpecificationSetMismatch(pSource, pDest)
{
	if (!pSource || !pDest || !pSource.Known)
	{
		return false;
	}
	if (pDest.Known && ((pSource.ItemIDTestSpecificationSet !== pDest.ItemIDTestSpecificationSet) || (pSource.MaterialIDTestSpecificationSet !== pDest.MaterialIDTestSpecificationSet)))
	{
		return true;
	}
	return pDest.PayItemSetIDs.some((pID) => pID !== pSource.ItemIDTestSpecificationSet);
}

/**
 * Flatten the service's specification set report into template-ready fields.
 * @param {Record<string, any>} pSpecSets - reportData.specificationSets from SamplePlanService.
 * @return {Record<string, any>|null} Display fields, or null when the report carries none.
 */
function prepareSpecificationSetView(pSpecSets)
{
	if (!pSpecSets || !pSpecSets.Alignment)
	{
		return null;
	}
	const tmpNames = pSpecSets.Names || {};
	const tmpAlignment = pSpecSets.Alignment;
	const tmpSource = formatProjectSets(pSpecSets.Source, tmpNames);
	const tmpDest = formatProjectSets(pSpecSets.Dest, tmpNames);
	const tmpContract = tmpAlignment.Contract;
	const tmpShared = tmpContract ? tmpContract.SharedWithProjects : [];
	return (
		{
			Crossing: tmpAlignment.Crossing === true,
			ShowChanges: (tmpAlignment.Crossing === true) && (tmpAlignment.Flip !== false),
			ShowKept: (tmpAlignment.Crossing === true) && (tmpAlignment.Flip === false),
			SourceItemLabel: tmpSource.ItemLabel,
			SourceMaterialLabel: tmpSource.MaterialLabel,
			SourceBasisLabel: tmpSource.BasisLabel,
			DestItemLabel: tmpDest.ItemLabel,
			DestMaterialLabel: tmpDest.MaterialLabel,
			DestBasisLabel: tmpDest.BasisLabel,
			TargetItemLabel: formatSetLabel(tmpAlignment.ItemIDTestSpecificationSet, tmpNames),
			HasContractUpdate: !!tmpContract,
			NoContract: (tmpAlignment.Crossing === true) && !pSpecSets.Dest.Contract,
			ContractLabel: tmpContract ? `${tmpContract.Name || 'Contract'} [${tmpContract.IDContract}]` : '',
			ContractItemBefore: tmpContract ? formatSetLabel(tmpContract.Before.ItemIDTestSpecificationSet, tmpNames) : '',
			ContractItemAfter: tmpContract ? formatSetLabel(tmpContract.After.ItemIDTestSpecificationSet, tmpNames) : '',
			ContractMaterialBefore: tmpContract ? formatSetLabel(tmpContract.Before.MaterialIDTestSpecificationSet, tmpNames) : '',
			ContractMaterialAfter: tmpContract ? formatSetLabel(tmpContract.After.MaterialIDTestSpecificationSet, tmpNames) : '',
			ContractShared: tmpShared.length > 0,
			SharedProjectCount: tmpShared.length,
			SharedProjects: tmpShared.map((pProject) => ({ Value: `${pProject.Name || '(unnamed)'} [${pProject.IDProject}]` })),
			RemapCount: tmpAlignment.LineItemRemaps.length,
			Remaps: tmpAlignment.LineItemRemaps.map((pRemap) => (
				{
					ItemCode: pRemap.ItemCode,
					Name: pRemap.Name,
					From: formatSetLabel(pRemap.FromIDTestSpecificationSet, tmpNames),
					To: formatSetLabel(tmpAlignment.ItemIDTestSpecificationSet, tmpNames),
				})),
			HasUnmapped: tmpAlignment.Unmapped.length > 0,
			UnmappedCount: tmpAlignment.Unmapped.length,
			Unmapped: tmpAlignment.Unmapped,
		});
}

/**
 * The confirmation dialog's two count tables. The contract and pay item rows appear only when the copy changes them.
 * @param {Record<string, number>} pStats - reportData.stats from SamplePlanService.
 * @return {{ Changes: Array<CountRow>, Unchanged: Array<CountRow> }}
 */
function buildConfirmCountRows(pStats)
{
	const tmpChanges = [
		{ Label: ENTITY_LABELS.Contract, Count: pStats.contractUpdateCount || 0 },
		{ Label: ENTITY_LABELS.LineItem, Count: pStats.lineItemRemapCount || 0 },
		{ Label: ENTITY_LABELS.ProjectLineItemMaterialTest, Count: pStats.plimtCount },
		{ Label: ENTITY_LABELS.MaterialLineItemJoin, Count: pStats.mljCount },
	].filter((pRow, pIndex) => (pIndex > 1) || (pRow.Count > 0));
	const tmpUnchanged = [
		{ Label: 'Sample plan tests in the source', Count: pStats.sourcePlimtCount || 0 },
		{ Label: 'Source tests not copied (item code not on the destination)', Count: pStats.unmappedSourcePlimtCount || 0 },
		{ Label: 'Repeated source tests, copied once', Count: pStats.collapsedPlimtCount || 0 },
		{ Label: 'Sample plan tests already on the destination (kept)', Count: pStats.destExistingPlimtCount || 0 },
		{ Label: 'Line item materials already on the destination (kept)', Count: pStats.destExistingMljCount || 0 },
	];
	return { Changes: tmpChanges, Unchanged: tmpUnchanged };
}

/**
 * Add display labels to the per-entity load results.
 * @param {Array<Record<string, any>>} pEntities - loadResult.entities from SamplePlanService.loadComprehension.
 * @return {Array<Record<string, any>>} Copies with Label and StatusLabel.
 */
function labelLoadEntities(pEntities)
{
	return pEntities.map((pEntity) => Object.assign({}, pEntity,
		{
			Label: ENTITY_LABELS[pEntity.Entity] || pEntity.Entity,
			StatusLabel: LOAD_STATUS_LABELS[pEntity.Status] || pEntity.Status,
		}));
}

module.exports = (
	{
		ENTITY_LABELS,
		LOAD_STATUS_LABELS,
		formatSetLabel,
		formatProjectSets,
		isSpecificationSetMismatch,
		prepareSpecificationSetView,
		buildConfirmCountRows,
		labelLoadEntities,
	});
